/**
 * Flujo de compra por pasos (Optioutlet).
 *
 * Tres paneles en /pages/envio -- Cómo te llamas, A dónde va, Contacto -- y de
 * ahí al checkout de Shopify. Los datos viajan por querystring: los
 * nombres de parámetro están verificados contra la tienda, y con ellos el
 * checkout llega prellenado y colapsa contacto y entrega, de modo que al
 * cliente solo le queda la tarjeta.
 *
 * Un solo <form> con los paneles alternandose. Partirlo en tres formas
 * habría obligado a sincronizar el estado a mano; así el envío final ya
 * tiene todo junto.
 */
(() => {
  const forma = document.querySelector('[data-ol-envio]');
  if (!forma) return;

  const CLAVE = 'ol-envio-datos';
  const selectPais = forma.querySelector('[data-ol-pais]');
  const selectEstado = forma.querySelector('[data-ol-estado]');
  const campoEstado = forma.querySelector('[data-ol-estado-campo]');
  const etiquetaEstado = forma.querySelector('[data-ol-estado-etiqueta]');

  const paneles = [...forma.querySelectorAll('[data-ol-panel]')];
  const slugs = paneles.map((p) => p.dataset.olPanel);
  const barra = document.querySelector('[data-ol-pasos]');
  const resumen = barra?.querySelector('[data-ol-pasos-resumen]');

  /** Shopify entrega el nombre del país como value, que es justo lo que
   *  espera checkout[shipping_address][country]. */
  const esMexico = () => selectPais?.value === 'Mexico' || selectPais?.value === 'México';

  /**
   * Rellena el select de estados con los del país elegido. Shopify los
   * publica en data-provinces de cada <option> como pares [valor,
   * etiqueta]. Si el país no tiene estados, el campo entero se esconde:
   * un select vacío solo confunde.
   */
  function pintarEstados(valorPrevio) {
    if (!selectPais || !selectEstado || !campoEstado) return;

    const opcion = selectPais.selectedOptions[0];
    let estados = [];
    try {
      estados = JSON.parse(opcion?.dataset.provinces || '[]');
    } catch {
      estados = [];
    }

    campoEstado.hidden = estados.length === 0;
    selectEstado.disabled = estados.length === 0;
    selectEstado.innerHTML = '';

    if (!estados.length) return;

    // México los llama estados; el resto del mundo, no necesariamente.
    if (etiquetaEstado) etiquetaEstado.textContent = esMexico() ? 'Estado' : 'Estado o provincia';

    for (const [valor, etiqueta] of estados) {
      const o = document.createElement('option');
      o.value = valor;
      o.textContent = etiqueta;
      selectEstado.append(o);
    }

    if (valorPrevio && estados.some(([valor]) => valor === valorPrevio)) {
      selectEstado.value = valorPrevio;
    }
  }

  function marcarError(input, mensaje) {
    const campo = input.closest('.ol-envio__campo');
    const aviso = forma.querySelector(`[data-error-de="${input.id}"]`);
    campo?.classList.toggle('ol-envio__campo--malo', Boolean(mensaje));
    input.setAttribute('aria-invalid', mensaje ? 'true' : 'false');
    if (aviso) {
      aviso.textContent = mensaje || '';
      aviso.hidden = !mensaje;
    }
  }

  const soloDigitos = (valor) => (valor || '').replace(/\D/g, '');

  /** Devuelve el mensaje de error, o null si el campo está bien. */
  function revisar(input) {
    const valor = (input.value || '').trim();

    if (input.required && !valor) return 'Falta este dato.';
    if (!valor) return null;

    if (input.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(valor)) {
      return 'Revisa el correo, parece incompleto.';
    }

    if (input.name === 'zip' && esMexico() && soloDigitos(valor).length !== 5) {
      return 'El código postal mexicano lleva 5 dígitos.';
    }

    if (input.name === 'phone') {
      const minimo = esMexico() ? 10 : 7;
      if (soloDigitos(valor).length < minimo) return 'El teléfono parece incompleto.';
    }

    return null;
  }

  /**
   * Los campos de una raíz. Se mira [hidden] en el .ol-envio__campo y no
   * en cualquier ancestro: el panel que no toca también está oculto, y sus
   * datos sí tienen que viajar al checkout.
   */
  function camposDe(raiz) {
    return [...raiz.querySelectorAll('input[name], select[name]')].filter(
      (e) => !e.disabled && !e.closest('.ol-envio__campo')?.hidden
    );
  }

  /** Valida una raíz y devuelve el primer campo malo, o null. */
  function validar(raiz) {
    let primerMalo = null;
    for (const input of camposDe(raiz)) {
      const mensaje = revisar(input);
      marcarError(input, mensaje);
      if (mensaje && !primerMalo) primerMalo = input;
    }
    return primerMalo;
  }

  // ---------------------------------------------------------------- pasos

  let actual = 0;

  /** El primer panel que todavía no está completo. Sin marcar errores:
   *  solo sirve para decidir hasta dónde se puede saltar. */
  function primerIncompleto() {
    for (let i = 0; i < paneles.length; i += 1) {
      if (camposDe(paneles[i]).some((input) => revisar(input))) return i;
    }
    return paneles.length - 1;
  }

  function pintarBarra(indice) {
    if (!barra) return;
    const pasoActual = Number(paneles[indice].dataset.olPaso);

    for (const li of barra.querySelectorAll('.ol-pasos__paso')) {
      const n = Number(li.dataset.paso);
      const estado = n < pasoActual ? 'hecho' : n === pasoActual ? 'actual' : 'pendiente';
      li.classList.remove('ol-pasos__paso--hecho', 'ol-pasos__paso--actual', 'ol-pasos__paso--pendiente');
      li.classList.add(`ol-pasos__paso--${estado}`);
      if (estado === 'actual') li.setAttribute('aria-current', 'step');
      else li.removeAttribute('aria-current');

      // Los pasos ya cubiertos se vuelven a abrir; los de adelante, no.
      const enlace = li.querySelector('.ol-pasos__enlace');
      const slug = li.dataset.slug;
      if (enlace && slug) {
        if (estado === 'hecho') enlace.setAttribute('href', `#${slug}`);
        else enlace.removeAttribute('href');
      }
    }

    if (resumen) {
      const texto = barra.querySelector(`.ol-pasos__paso[data-paso="${pasoActual}"] .ol-pasos__texto`);
      const total = barra.querySelectorAll('.ol-pasos__paso').length;
      resumen.textContent = `Paso ${pasoActual} de ${total} · ${texto?.textContent.trim() ?? ''}`;
    }
  }

  function mostrar(indice, { empujar = true, foco = true } = {}) {
    actual = Math.max(0, Math.min(indice, paneles.length - 1));

    paneles.forEach((panel, i) => {
      panel.hidden = i !== actual;
    });
    pintarBarra(actual);

    const slug = slugs[actual];
    if (empujar && location.hash.slice(1) !== slug) {
      history.pushState({ olPaso: slug }, '', `#${slug}`);
    }

    if (foco) {
      const primero = camposDe(paneles[actual])[0];
      primero?.focus({ preventScroll: true });
      paneles[actual].scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
  }

  function avanzar() {
    const malo = validar(paneles[actual]);
    if (malo) {
      malo.focus({ preventScroll: true });
      malo.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return false;
    }
    guardar();
    if (actual < paneles.length - 1) {
      mostrar(actual + 1);
      return false;
    }
    return true;
  }

  // -------------------------------------------------------------- memoria

  /** Los nombres de parámetro que entiende el checkout de Shopify. */
  const DESTINO = {
    email: 'checkout[email]',
    first_name: 'checkout[shipping_address][first_name]',
    last_name: 'checkout[shipping_address][last_name]',
    address1: 'checkout[shipping_address][address1]',
    address2: 'checkout[shipping_address][address2]',
    zip: 'checkout[shipping_address][zip]',
    city: 'checkout[shipping_address][city]',
    province: 'checkout[shipping_address][province]',
    country: 'checkout[shipping_address][country]',
    phone: 'checkout[shipping_address][phone]',
  };

  function recolectar() {
    const datos = {};
    for (const input of camposDe(forma)) {
      const valor = (input.value || '').trim();
      if (valor) datos[input.name] = valor;
    }
    return datos;
  }

  function guardar() {
    try {
      localStorage.setItem(CLAVE, JSON.stringify(recolectar()));
    } catch {
      /* sin almacenamiento, nada que guardar */
    }
  }

  // Si el cliente regresa a corregir algo, que no vuelva a teclear todo.
  let guardado = {};
  try {
    guardado = JSON.parse(localStorage.getItem(CLAVE) || '{}');
  } catch {
    guardado = {};
  }

  for (const [nombre, valor] of Object.entries(guardado)) {
    const input = forma.querySelector(`[name="${nombre}"]`);
    if (input && input !== selectEstado && input !== selectPais) input.value = valor;
  }

  /**
   * country_option_tags intercala separadores "---" entre los países
   * frecuentes y el resto. Son opciones reales y seleccionables, y una
   * de ellas puede quedar elegida de entrada, lo que deja el select de
   * estados vacío. Se inutilizan y se arranca en México.
   */
  if (selectPais) {
    for (const opcion of selectPais.options) {
      if (opcion.value.startsWith('---')) opcion.disabled = true;
    }

    const deseado = guardado.country || 'Mexico';
    const existe = [...selectPais.options].some((o) => o.value === deseado && !o.disabled);
    selectPais.value = existe ? deseado : 'Mexico';
    if (!selectPais.value || selectPais.selectedOptions[0]?.disabled) {
      selectPais.selectedIndex = [...selectPais.options].findIndex((o) => !o.disabled);
    }
  }

  pintarEstados(guardado.province);

  selectPais?.addEventListener('change', () => pintarEstados());

  // -------------------------------------------------------------- eventos

  forma.addEventListener('input', (evento) => {
    const input = evento.target;
    if (input.getAttribute('aria-invalid') === 'true') marcarError(input, revisar(input));
  });

  forma.addEventListener('click', (evento) => {
    if (evento.target.closest('[data-ol-siguiente]')) {
      evento.preventDefault();
      avanzar();
    } else if (evento.target.closest('[data-ol-atras]')) {
      evento.preventDefault();
      mostrar(actual - 1);
    }
  });

  // Clic en un paso ya cubierto de la barra.
  barra?.addEventListener('click', (evento) => {
    const enlace = evento.target.closest('.ol-pasos__enlace[href^="#"]');
    if (!enlace) return;
    const slug = enlace.getAttribute('href').slice(1);
    const destino = slugs.indexOf(slug);
    if (destino === -1) return;
    evento.preventDefault();
    mostrar(destino);
  });

  window.addEventListener('popstate', () => {
    const destino = slugs.indexOf(location.hash.slice(1));
    mostrar(destino === -1 ? 0 : destino, { empujar: false });
  });

  forma.addEventListener('submit', (evento) => {
    evento.preventDefault();

    // Enter en un campo del primer o segundo panel dispara submit: ahí no
    // toca pagar todavia, toca avanzar.
    if (actual < paneles.length - 1) {
      avanzar();
      return;
    }

    // Último panel: se revisa todo, no solo lo que está a la vista, por si
    // alguien llegó hasta aquí con el hash.
    for (let i = 0; i < paneles.length; i += 1) {
      const malo = validar(paneles[i]);
      if (malo) {
        mostrar(i, { foco: false });
        malo.focus({ preventScroll: true });
        malo.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }
    }

    guardar();

    const parametros = new URLSearchParams();
    for (const [nombre, valor] of Object.entries(recolectar())) {
      if (DESTINO[nombre]) parametros.set(DESTINO[nombre], valor);
    }

    const boton = paneles[actual].querySelector('.ol-envio__continuar');
    boton?.setAttribute('aria-busy', 'true');

    const raiz = window.Shopify?.routes?.root ?? '/';
    const destino = `${raiz}checkout`.replace('//', '/');
    window.location.assign(`${destino}?${parametros.toString()}`);
  });

  // Arranque: se respeta el hash, pero sin brincar por encima de un paso
  // incompleto -- si alguien guarda /pages/envio#contacto con el
  // formulario vacío, aterriza donde de verdad le toca.
  const pedido = slugs.indexOf(location.hash.slice(1));
  const tope = primerIncompleto();
  mostrar(pedido === -1 ? 0 : Math.min(pedido, tope), { empujar: false, foco: false });
})();
